/**
 * Экспорт паттернов в ZIP-бандл для Fantom PTN UTILITY → IMPORT.
 *
 * Конвенция имён файлов — по Roland Reference PDF p.131:
 *   `${projectName}_{track:02d}-{slot}.MID`
 * (например, `MyPTN_01-A.MID`). `PatternId` (`T{track}-{slot}`)
 * используется только для in-memory идентификации — в имени файла
 * `T`-префикса нет, чтобы 1-к-1 совпадать с тем, что показывает
 * on-device file browser.
 *
 * Layout ZIP: все `.MID` файлы лежат в корне, `manifest.json` —
 * в подпапке `_meta/`. Так пользователь копирует содержимое ZIP
 * на USB и сразу видит `.MID` файлы на верхнем уровне; `manifest.json`
 * не мозолит глаза в file browser.
 *
 * Использует собственный minimal ZIP writer (метод 0, stored,
 * без сжатия) — стандартный формат, читается любым unzip-ом.
 * Реализация через fflate не подошла: его browser build проверяет
 * `instanceof u8` против локального класса, а глобальный `Uint8Array`
 * от `strToU8` не проходит эту проверку (zip превращается в
 * вложенную директорию).
 */

import { encodeMidi } from "@midi-fantom-utils/midi-core";
import {
  type Pattern,
  type PatternManifest,
  type PatternManifestEntry,
  type PatternSlot,
  type TrackNumber,
} from "./types.js";

export type ExportPatternsZipOptions = {
  projectName: string;
  patterns: ReadonlyArray<Pattern>;
  now?: () => Date;
};

const sanitize = (name: string): string => {
  return name
    .replace(/[\\/:*?"<>|]+/g, "_")
    .replace(/\s+/g, "_")
    .trim();
};

const fileNameFor = (projectName: string, track: TrackNumber, slot: PatternSlot): string => {
  const base = sanitize(projectName) || "PTN";
  const t = String(track).padStart(2, "0");
  return `${base}_${t}-${slot}.MID`;
};

/**
 * Создаёт stored-only ZIP (метод 0, без сжатия). Каждый файл
 * представлен local file header + данными; в конце — central directory
 * + end-of-central-directory record.
 */
const buildStoredZip = (entries: { name: Uint8Array; data: Uint8Array }[]): Uint8Array => {
  const enc = new TextEncoder();
  const localHeaders: Uint8Array[] = [];
  const centralHeaders: Uint8Array[] = [];
  let offset = 0;

  for (const entry of entries) {
    const name = entry.name;
    const data = entry.data;
    const crc = crc32(data);
    const size = data.length;

    // Local file header
    const lh = new Uint8Array(30 + name.length);
    const lv = new DataView(lh.buffer);
    lv.setUint32(0, 0x04034b50, true); // signature
    lv.setUint16(4, 20, true); // version needed
    lv.setUint16(6, 0, true); // flags
    lv.setUint16(8, 0, true); // method = stored
    lv.setUint16(10, 0, true); // mod time
    lv.setUint16(12, 0, true); // mod date
    lv.setUint32(14, crc, true);
    lv.setUint32(18, size, true); // compressed size
    lv.setUint32(22, size, true); // uncompressed size
    lv.setUint16(26, name.length, true);
    lv.setUint16(28, 0, true); // extra field length
    lh.set(name, 30);
    localHeaders.push(lh, data);

    // Central directory header
    const ch = new Uint8Array(46 + name.length);
    const cv = new DataView(ch.buffer);
    cv.setUint32(0, 0x02014b50, true); // signature
    cv.setUint16(4, 20, true); // version made by
    cv.setUint16(6, 20, true); // version needed
    cv.setUint16(8, 0, true); // flags
    cv.setUint16(10, 0, true); // method
    cv.setUint16(12, 0, true); // mod time
    cv.setUint16(14, 0, true); // mod date
    cv.setUint32(16, crc, true);
    cv.setUint32(20, size, true); // compressed size
    cv.setUint32(24, size, true); // uncompressed size
    cv.setUint16(28, name.length, true);
    cv.setUint16(30, 0, true); // extra
    cv.setUint16(32, 0, true); // comment
    cv.setUint16(34, 0, true); // disk
    cv.setUint16(36, 0, true); // internal attrs
    cv.setUint32(38, 0, true); // external attrs
    cv.setUint32(42, offset, true); // local header offset
    ch.set(name, 46);
    centralHeaders.push(ch);

    offset += lh.length + data.length;
  }

  // Central directory
  const cdSize = centralHeaders.reduce((s, b) => s + b.length, 0);
  const cd = new Uint8Array(cdSize);
  let cdOff = 0;
  for (const b of centralHeaders) {
    cd.set(b, cdOff);
    cdOff += b.length;
  }

  // End of central directory
  const eocd = new Uint8Array(22);
  const ev = new DataView(eocd.buffer);
  ev.setUint32(0, 0x06054b50, true);
  ev.setUint16(4, 0, true); // disk
  ev.setUint16(6, 0, true); // start disk
  ev.setUint16(8, entries.length, true); // entries on this disk
  ev.setUint16(10, entries.length, true); // total entries
  ev.setUint32(12, cdSize, true);
  ev.setUint32(16, offset, true); // cd offset
  ev.setUint16(20, 0, true); // comment length

  // Concat all
  const totalSize = offset + cdSize + 22;
  const out = new Uint8Array(totalSize);
  let off = 0;
  for (const b of localHeaders) {
    out.set(b, off);
    off += b.length;
  }
  out.set(cd, off);
  off += cdSize;
  out.set(eocd, off);
  void enc;
  return out;
};

const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let i = 0; i < 256; i += 1) {
    let c = i;
    for (let k = 0; k < 8; k += 1) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    t[i] = c >>> 0;
  }
  return t;
})();

const crc32 = (data: Uint8Array): number => {
  let c = 0xffffffff;
  for (let i = 0; i < data.length; i += 1) {
    c = (c >>> 8) ^ CRC_TABLE[(c ^ data[i]!) & 0xff]!;
  }
  return (c ^ 0xffffffff) >>> 0;
};

export const exportPatternsZip = (options: ExportPatternsZipOptions): Uint8Array => {
  const { projectName, patterns } = options;
  const now = options.now ?? (() => new Date());

  const fileEntries: Record<string, Uint8Array> = {};
  const manifestEntries: PatternManifestEntry[] = [];
  const seenNames = new Set<string>();

  for (const pattern of patterns) {
    let fileName = fileNameFor(projectName, pattern.track, pattern.slot);
    let counter = 1;
    while (seenNames.has(fileName)) {
      const idx = fileName.lastIndexOf(".");
      fileName =
        idx >= 0
          ? `${fileName.slice(0, idx)}_${counter}${fileName.slice(idx)}`
          : `${fileName}_${counter}`;
      counter += 1;
    }
    seenNames.add(fileName);
    fileEntries[fileName] = encodeMidi(pattern.midi);
    manifestEntries.push({
      track: pattern.track,
      slot: pattern.slot,
      file: fileName,
      lengthMeasures: pattern.lengthMeasures,
      noteCount: pattern.noteCount,
    });
  }

  const manifest: PatternManifest = {
    project: projectName,
    generatedAt: now().toISOString(),
    patterns: manifestEntries,
  };
  fileEntries["_meta/manifest.json"] = new TextEncoder().encode(
    JSON.stringify(manifest, null, 2)
  );

  const enc = new TextEncoder();
  const entries = Object.entries(fileEntries).map(([name, data]) => ({
    name: enc.encode(name),
    data,
  }));
  return buildStoredZip(entries);
};
